import { addImportMapHash } from '@ms-cloudpack/import-map';
import fsPromises from 'fs/promises';
import type { BundleRequest } from '../types/BundleRequest.js';
import type { Task } from '../types/Task.js';
import { bundleTask, type BundleTaskContext, type BundleTaskOptions } from './bundleTask.js';
import type { PartialContext } from '../types/Context.js';

/**
 * Create a bundle task (internally calls `bundleTask()`) that can be executed by the server's TaskRunner.
 * This allows active tasks to be tracked remotely (e.g. in the browser) and allows the user to cancel tasks.
 */
export function createBundleTask(
  options: BundleTaskOptions,
  context: BundleTaskContext &
    PartialContext<'packageHashes', keyof BundleTaskContext['session'] | 'importMap' | 'packageImportPaths' | 'urls'>,
): Task<BundleRequest> {
  const { bundleRequest } = options;
  const { id, packageName, version, packagePath, isExternal, targetEnvironment } = bundleRequest;
  const { packageHashes, session } = context;
  const { hash, sourceHash } = options;
  const name = `Bundle ${packageName}@${version}`;

  return {
    // Name for task runner logging.
    name,
    id,
    async execute(): Promise<BundleRequest> {
      await bundleTask(options, context);

      try {
        // Add a hash to the import map for this package.
        // (Read the session properties here rather than above because they might not be set yet.)
        if (session.importMap && session.urls.bundleServer && session.urls.appServer) {
          const importMapHash =
            sourceHash ||
            hash ||
            (await packageHashes.get({ packagePath, isSourceHashingEnabled: !isExternal, targetEnvironment }));
          addImportMapHash({ packageName, version, hash: importMapHash }, context);
        }
      } catch (e) {
        console.error(`Error adding hash to import map for ${bundleRequest.packagePath}:`, (e as Error).stack || e);
      }

      return bundleRequest;
    },
    clear: async () => {
      if (!bundleRequest.result) {
        return;
      }

      bundleRequest.result.dispose?.();

      try {
        if (bundleRequest.result.outputPath) {
          await fsPromises.rm(bundleRequest.result.outputPath, { recursive: true, force: true, maxRetries: 2 });
        }
      } catch {
        console.warn(
          `Error clearing bundle output path (likely due to a file lock): ${bundleRequest.result.outputPath}`,
        );
      }

      bundleRequest.result = undefined;
    },
    dispose: () => {
      bundleRequest.result?.dispose?.();
    },
    getStartDescription: () => ({
      inputPath: packagePath,
    }),
    getEndDescription: (taskResult) => ({
      errors: taskResult?.result?.errors,
      warnings: taskResult?.result?.warnings,
      outputPath: bundleRequest.result?.outputPath,
    }),
  };
}
